type ABVariant = string | { name: string; weight?: number };

export type ABTestConfig = {
  key: string;
  variants: ABVariant[];
  pages: string[];
  version?: number;
};

export type ABTestConfigs = Record<string, ABTestConfig>;

type ABTestManagerOptions = {
  scopeKey?: string;
};

type PageTestEntry = {
  name: string;
  version: number;
  key: string;
  variants: ABVariant[];
};

const DEFAULT_SCOPE = "global";

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

export function safeGet(key: string): string | undefined {
  if (!isBrowser()) return undefined;
  try {
    return window.localStorage.getItem(key) ?? undefined;
  } catch (error) {
    console.warn("AB test storage read failed:", error);
    return undefined;
  }
}

export function safeSet(key: string, value: string): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.warn("AB test storage write failed:", error);
  }
}

/**
 * Staging hosting deploys against the production Firebase project (see
 * deploy:hosting:staging), so PROD/VITE_FIREBASE_PROJECT_ID can't tell
 * staging apart from real production — VITE_DEPLOY_TARGET can.
 *
 * Exported so other URL-based overrides that don't go through a registered
 * ABTestConfig (e.g. scriptedChatVariantOverride.ts) can reuse the exact
 * same dev/staging gate instead of re-deriving it.
 */
export function isVariantOverrideAllowed(): boolean {
  if (typeof import.meta === "undefined") return false;
  const env = import.meta.env;
  return (
    env?.DEV === true ||
    env?.VITE_DEPLOY_TARGET === "staging" ||
    env?.VITE_ENABLE_AB_OVERRIDE === "true"
  );
}

function readVariantOverride(testName: string, validVariants: string[]): string | null {
  if (!isBrowser() || !isVariantOverrideAllowed()) return null;
  try {
    const forced = new URLSearchParams(window.location.search).get(`ab_${testName}`);
    if (!forced) return null;
    if (validVariants.includes(forced)) return forced;
    console.warn(
      `[ABTest] Ignoring "?ab_${testName}=${forced}" — valid variants: ${validVariants.join(", ")}`
    );
    return null;
  } catch (error) {
    console.warn("AB test override read failed:", error);
    return null;
  }
}

/**
 * localStorage key a test's assignment is stored under.
 *
 * `config.version` is the re-bucketing lever. Bumping it changes the storage
 * key, so every visitor is assigned fresh by the current weights — including
 * one who still carries a variant name from the previous version of the test,
 * which `getOrCreateVariant` would otherwise keep forever (it only re-rolls
 * when the stored value is not a registered name). Use it whenever a test is
 * relaunched with different variants or weights and the old buckets must not
 * leak into the new one.
 *
 * Version 1 (the default, i.e. `version` omitted) keeps the historical
 * un-suffixed key, so this is additive: no existing test's storage moves.
 */
export function getVariantStorageKey(scopeKey: string, config: ABTestConfig): string {
  const version = config.version ?? 1;
  const versionSuffix = version > 1 ? `_v${version}` : "";
  return `${scopeKey}_${config.key}${versionSuffix}`;
}

function getVariantName(variant: ABVariant): string {
  return typeof variant === "string" ? variant : variant.name;
}

function normalizeVariants(variants: ABVariant[]): string[] {
  return variants.map((variant) => getVariantName(variant));
}

function hasUnderscore(value: string): boolean {
  return value.includes("_");
}

function assertValidConfig(testName: string, config: ABTestConfig): void {
  const errors: string[] = [];

  if (!config.key) {
    errors.push("missing key");
  } else if (config.key !== `${testName}Key`) {
    errors.push(`key must be ${testName}Key`);
  }

  if (!Array.isArray(config.variants) || config.variants.length === 0) {
    errors.push("variants must be a non-empty array");
  } else {
    const variantNames = normalizeVariants(config.variants);
    const unique = new Set(variantNames);
    if (unique.size !== variantNames.length) {
      errors.push("variants must be unique");
    }
    if (variantNames.some((name) => hasUnderscore(name))) {
      errors.push("variant names must not include underscores");
    }
  }

  if (!Array.isArray(config.pages) || config.pages.length === 0) {
    errors.push("pages must be a non-empty array");
  }

  if (errors.length > 0) {
    const message = `Invalid AB test config for ${testName}: ${errors.join(
      ", "
    )}`;
    const isProd =
      typeof import.meta !== "undefined" && import.meta.env?.PROD === true;
    if (isProd) {
      console.warn(message);
    } else {
      throw new Error(message);
    }
  }
}

function selectWeightedVariant(variants: ABVariant[]): ABVariant {
  const weighted = variants.map((variant) => {
    if (typeof variant === "string") {
      return { name: variant, weight: 1 };
    }
    return {
      name: variant.name,
      weight: typeof variant.weight === "number" ? variant.weight : 1,
    };
  });

  const totalWeight = weighted.reduce((sum, entry) => sum + entry.weight, 0);
  const threshold = Math.random() * totalWeight;
  let running = 0;

  for (const entry of weighted) {
    running += entry.weight;
    if (threshold <= running) {
      return entry;
    }
  }

  return weighted[weighted.length - 1];
}

export class ABTestManager {
  private configs: ABTestConfigs;
  private scopeKey: string;
  private keys: Record<string, string>;
  private pageTests: Record<string, PageTestEntry[]>;
  private loggedTests: Set<string>;

  constructor(configs: ABTestConfigs, options: ABTestManagerOptions = {}) {
    this.configs = configs;
    this.scopeKey = options.scopeKey ?? DEFAULT_SCOPE;
    this.keys = {};
    this.pageTests = {};
    this.loggedTests = new Set();

    Object.entries(this.configs).forEach(([name, config]) => {
      assertValidConfig(name, config);
      this.keys[config.key] = this.getOrCreateVariant(name, config);
    });

    this.buildPageTestsMap();

    const testEntries = Object.entries(this.configs);
    if (isVariantOverrideAllowed() && testEntries.length > 0) {
      const summary = testEntries
        .map(([name, config]) => `${name}=${this.keys[config.key]}`)
        .join(", ");
      const [exampleName, exampleConfig] = testEntries[0];
      const exampleVariant = normalizeVariants(exampleConfig.variants)[0];
      console.info(
        `[ABTest] scope="${this.scopeKey}" — ${summary}. Force with ?ab_<testName>=<variant>, e.g. ?ab_${exampleName}=${exampleVariant}`
      );
    }
  }

  private getOrCreateVariant(testName: string, config: ABTestConfig): string {
    const prefixedKey = getVariantStorageKey(this.scopeKey, config);
    const variants = config.variants;
    const variantNames = normalizeVariants(variants);

    const override = readVariantOverride(testName, variantNames);
    if (override) {
      safeSet(prefixedKey, override);
      return override;
    }

    let key = safeGet(prefixedKey);

    if (!key || !variantNames.includes(key)) {
      const selected = selectWeightedVariant(variants);
      key = getVariantName(selected);
      safeSet(prefixedKey, key);
    }

    return key;
  }

  private buildPageTestsMap(): void {
    this.pageTests = Object.entries(this.configs).reduce<Record<string, PageTestEntry[]>>(
      (acc, [name, config]) => {
        config.pages.forEach((page) => {
          if (!acc[page]) acc[page] = [];
          acc[page].push({
            name,
            version: config.version || 1,
            key: this.keys[config.key],
            variants: config.variants,
          });
        });
        return acc;
      },
      {}
    );
  }

  getAllKeys(): Record<string, string> {
    return { ...this.keys };
  }

  getVariant(testName: string): string {
    const config = this.configs[testName];
    if (!config) return "default";
    return this.keys[config.key];
  }

  getABTests(pageKey?: string): string[] {
    if (!pageKey) return [];

    const pageTests = this.pageTests[pageKey] || [];

    return pageTests
      .filter((test) => test.variants.length > 1)
      .map((test) => {
        const versionSuffix = test.version > 1 ? `-v${test.version}` : "";
        return `${test.name}${versionSuffix}_${test.key}`;
      })
      .filter((test) => {
        if (this.loggedTests.has(test)) {
          return false;
        }
        this.loggedTests.add(test);
        return true;
      });
  }
}

export function createABTestManager(
  configs: ABTestConfigs,
  options: ABTestManagerOptions = {}
): ABTestManager {
  return new ABTestManager(configs, options);
}

/**
 * Reads a stored assignment without instantiating a manager. `version` must
 * match the `version` on the test's ABTestConfig (see getVariantStorageKey) —
 * a versioned test stores under a suffixed key, so passing the wrong version
 * silently returns "default".
 */
export function getABTestVariant(
  testName: string,
  scopeKey: string = DEFAULT_SCOPE,
  version = 1
): string {
  const storageKey = getVariantStorageKey(scopeKey, {
    key: `${testName}Key`,
    variants: [],
    pages: [],
    version,
  });
  return safeGet(storageKey) || "default";
}
